/**
 * Shared SSE stream reader — consolidates the buffer-split-on-`\n\n`,
 * `handled` flag / `fail()` pattern, AbortError swallow, and auth
 * check that were duplicated between chat.js and roundtable.js (G10).
 *
 * Each caller provides an `onEvent(data)` callback that receives one
 * parsed JSON object per SSE frame. The caller is responsible for
 * dispatching on event shape (token vs done vs error etc) — this
 * module owns only the transport + framing layer.
 *
 * Also exports two helpers used by both adapters:
 *   - `isAbortError(err)` — predicate for the abort-swallow pattern.
 *   - `taggedBudgetError(data)` — returns a tagged Error when the SSE
 *     frame carries a daily-budget exhaustion payload, else null.
 *
 * @param {Object} opts
 * @param {string} opts.url — full URL to POST to
 * @param {Object} opts.body — request body (will be JSON.stringify'd)
 * @param {AbortSignal} [opts.signal] — optional cancellation signal
 * @param {(data: Object) => void} opts.onEvent — called per parsed SSE frame
 * @param {(err: Error) => void} [opts.onError] — called at most once on failure
 */
import { getIdToken } from "./auth.js";
import { getActiveTenantHeader } from "./api.js";

/**
 * Predicate for the AbortError swallow pattern. Fetch and reader.read()
 * both surface a DOMException / Error whose `name` is "AbortError" when
 * the caller's AbortSignal fires — the SSE adapters treat this as a
 * clean cancellation rather than an error.
 */
export function isAbortError(err) {
  return err?.name === "AbortError";
}

/**
 * Tag an SSE error frame as a budget-exhaustion error when the backend
 * payload carries the fatal-budget shape: `{ error, recoverable: false,
 * dailyTokenBudget: number, tokensUsedToday?: number, personaId? }`.
 *
 * Returns a `new Error(data.error)` with `kind: "budget"` plus the
 * budget fields mirrored on for UI rendering, or `null` if the frame
 * is not a budget-exhaustion payload. Callers route the tagged error
 * into whichever error channel they maintain (single-chat `onError`,
 * roundtable `onBudgetExhausted`).
 */
export function taggedBudgetError(data) {
  if (
    !data ||
    !data.error ||
    data.recoverable !== false ||
    typeof data.dailyTokenBudget !== "number"
  ) {
    return null;
  }
  const err = new Error(data.error);
  err.kind = "budget";
  err.tokensUsedToday = data.tokensUsedToday;
  err.dailyTokenBudget = data.dailyTokenBudget;
  if (data.personaId) err.personaId = data.personaId;
  return err;
}

export async function streamSSE({ url, body, signal, onEvent, onError }) {
  let handled = false;
  const fail = (err) => {
    if (handled) return;
    handled = true;
    onError?.(err);
  };

  const token = getIdToken();
  if (!token) {
    fail(new Error("Not authenticated"));
    return;
  }

  let response;
  try {
    // v5 hotfix — propagate the super-admin tenant override to the
    // chat / roundtable Function URL. Previously omitted because sse.js
    // built its own fetch independently of services/api.js, so a
    // super-admin chatting against a non-default tenant routed to the
    // wrong tenant context (project lookup failed → response error → the
    // browser surfaced the read failure as "NetworkError when attempting
    // to fetch resource"). Mirrors the api.js header injection.
    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
    const activeTenant = getActiveTenantHeader();
    if (activeTenant) headers["X-Tenant-Id"] = activeTenant;
    response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      signal,
    });
  } catch (err) {
    if (isAbortError(err)) return;
    fail(err);
    return;
  }

  if (!response.ok || !response.body) {
    const text = await response.text().catch(() => "");
    fail(new Error(`${response.status}: ${text || response.statusText}`));
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const events = buffer.split("\n\n");
      buffer = events.pop() ?? "";

      for (const event of events) {
        const line = event.trim();
        if (!line.startsWith("data: ")) continue;
        let data;
        try {
          data = JSON.parse(line.slice(6));
        } catch {
          continue;
        }

        // Let the caller handle the event. If onEvent returns:
        //   { bail: true }         — stop reading the stream
        //   { bail: true, error }  — stop reading and call fail(error)
        // This ensures all onError calls go through the `handled` guard.
        const result = onEvent(data);
        if (result?.bail) {
          if (result.error) fail(result.error);
          return;
        }
      }
    }
  } catch (err) {
    if (isAbortError(err)) return;
    fail(err);
  }
}
